import { useContext, useState, useRef, useCallback } from "react";
import { AuthContext, YearContext } from "../context/context";
import { addWish } from "../api";
import PageContentContainer from "../Components/ContentContainers/PageContentContainer";
import WishCard from "../Components/Cards/CardWish";
import AddWishForm from "../Components/Forms/AddWishForm";
import { useLanguage } from "../context/LanguageContext";
import Toast from "../Components/Elements/Toast";
import ActionCard from "../Components/Cards/ActionCard";
import Popup from "../Components/Elements/Popup";
import Headline from "../Components/Elements/Headline";
import StartMessage from "../Components/Elements/StartMessage";

export default function Wishlist() {
  const { userData, setUserData } = useContext(AuthContext);
  const { selectedYear } = useContext(YearContext);
  const { copy } = useLanguage();

  const [popupType, setPopupType] = useState(null);
  const openPopup = (type) => setPopupType(type);
  const closePopup = () => setPopupType(null);

  const [toast, setToast] = useState({ message: "", type: "success" });

  const formRef = useRef(null);
  const submitHandlerRef = useRef(null); // Ref used to programmatically trigger form submission

  const BASE_URL = import.meta.env.VITE_API_URL;

  // Handles submission of a new wish
  const handleSubmitWish = async (formData) => {
    try {
      const wishData = new FormData();
      wishData.append("wish_title", formData.wish_title);
      wishData.append("wish_img", formData.wish_img[0]);
      wishData.append("wish_url", formData.wish_url || "");
      wishData.append("wish_price", formData.wish_price);
      wishData.append("wish_currency", formData.wish_currency);
      wishData.append("year", selectedYear);
      wishData.append("user_id", userData.id);

      const response = await addWish(wishData, userData.id);

      if (!response || !response.success) {
        console.error("Failed to add wish:", response);
        setToast({ message: copy.wishlist.add.error, type: "error" });
        return;
      }

      const newWish = response.wish;

      // Update user data with the new wish
      setUserData((prevUserData) => ({
        ...prevUserData,
        wishlists: [newWish, ...(prevUserData.wishlists || [])],
      }));

      setToast({ message: copy.wishlist.add.success, type: "success" });
      closePopup();
    } catch (err) {
      console.error("Error submitting wish:", err);
      setToast({
        message: err.message || copy.wishlist.add.error_generic,
        type: "error",
      });
    }
  };

  // Filter wishes for selected year
  const wishData =
    selectedYear && userData?.wishlists
      ? userData.wishlists.filter((wish) => wish.year === selectedYear)
      : userData?.wishlists || [];

  // Sort wishes by status: incomplete first, then completed
  const sortedWishData = wishData.sort((a, b) => {
    if (a.status === true && b.status !== true) return 1;
    if (a.status !== true && b.status === true) return -1;
    return 0;
  });

  // Share Wishlist handler
  const handleShareWishlist = useCallback(async () => {
    let token = userData.wishlistPublicToken;
    if (!token) {
      // Generate token on demand
      try {
        const res = await fetch(`${BASE_URL}/wishlist/generate-public-token`, {
          method: "POST",
          credentials: "include",
        });
        const data = await res.json();
        if (!data.wishlistPublicToken) {
          setToast({
            message: copy.wishlist.share.error,
            type: "error",
          });
          return;
        }
        token = data.wishlistPublicToken;
        setUserData({ ...userData, wishlistPublicToken: token });
      } catch (err) {
        console.error(err);
        setToast({ message: copy.wishlist.share.error, type: "error" });
        return;
      }
    }
    const publicUrl = `${window.location.origin}/wishlist/public/${selectedYear}/${token}`;
    navigator.clipboard.writeText(publicUrl);
    setToast({ message: copy.wishlist.share.success, type: "success" });
  }, [userData, setUserData, selectedYear]);

  return (
    <>
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "success" })}
        />
      )}

      <PageContentContainer>
        {userData && userData.goals && userData.goals.length > 0 ? (
          <>
            <Headline
              headline={copy.wishlist.headline}
              description={copy.wishlist.description}
              buttonCTA={copy.wishlist.share.button}
              onClickLink={handleShareWishlist}
              type="button"
            />
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              <ActionCard openPopup={() => openPopup("add-wish")} />
              {sortedWishData &&
                sortedWishData.length > 0 &&
                sortedWishData.map((wishlistItem) => (
                  <WishCard
                    key={wishlistItem.id}
                    wishlistItem={wishlistItem}
                    onToast={setToast}
                  />
                ))}
            </div>

          </>
        ) : (
          <StartMessage
            title={copy.start_message.headline}
            message={copy.start_message.description}
          />
        )}
      </PageContentContainer>

      <Popup
        isOpen={popupType === "add-wish"}
        onClose={closePopup}
        title={copy.card_wishlist.popup.add_title}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.add}
        onClickPrimaryLink={() => {
          if (submitHandlerRef.current) {
            submitHandlerRef.current(); // Trigger form submission from parent
          }
        }}
      >
        <AddWishForm
          userData={userData}
          selectedYear={selectedYear}
          handleSubmitWish={handleSubmitWish}
          formRef={formRef}
          submitHandlerRef={submitHandlerRef}
        />
      </Popup>
    </>
  );
}
